/**
 * EmbedPreview — /embed-preview
 *
 * Marketing + sanity page for sister-site owners. Shows the widget
 * rendered live under 3 different filter combos + copy-paste snippets.
 */
import { useMemo, useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Code2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const SCENARIOS = [
  {
    id: "farming-oregon",
    title: "Farming · Oregon",
    tagline: "FarmersPulse drops this under any Oregon page.",
    attrs: { "data-category": "farming", "data-country": "USA", "data-state": "Oregon", "data-limit": "5" },
  },
  {
    id: "travel-japan",
    title: "Travel · Japan",
    tagline: "TravelerPulse embeds this on their Japan hub.",
    attrs: { "data-category": "travel", "data-country": "Japan", "data-limit": "5" },
  },
  {
    id: "food-portland",
    title: "Food · Portland",
    tagline: "FoodPulse drops this on their Portland listings.",
    attrs: { "data-category": "food", "data-city": "Portland", "data-limit": "5" },
  },
];

export default function EmbedPreview() {
  const origin = window.location.origin;
  // NOTE: we do NOT load /embed.js here — the preview mounts render via
  // `renderInline` below so the page has no external-script dependency.
  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="embed-preview">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2">
        <Code2 className="w-3 h-3" /> Sister-site widget
      </div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
        Embed ChatsPulse anywhere
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-2xl">
        Drop one line of HTML on any FarmersPulse, TravelerPulse, FoodPulse, Kerala.com, or Indias.com page and get a live scoped discussion strip — no keys, no cookies, no tracking. Every <code className="text-xs px-1 py-0.5 rounded bg-slate-100">data-*</code> filter maps to a query param on our timeline API.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {SCENARIOS.map((s) => (
          <ScenarioCard key={s.id} scenario={s} origin={origin} />
        ))}
      </div>

      <section className="mt-14" data-testid="embed-preview-docs">
        <h2 className="font-bold text-slate-900 text-lg mb-2">Every filter available</h2>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2">Attribute</th>
                <th className="text-left px-4 py-2">Example</th>
                <th className="text-left px-4 py-2">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[
                ["data-category", "farming", "Canonical topic slug"],
                ["data-country",  "USA",     "Country name (matches thread.location.country)"],
                ["data-state",    "Oregon",  "State/province name"],
                ["data-city",     "Portland","City name"],
                ["data-tag",      "Cherry Blossoms", "Free-form user tag"],
                ["data-kind",     "question","'discussion' | 'question'"],
                ["data-limit",    "5",       "How many threads to render (max 20)"],
                ["data-mount",    "#my-css-selector", "Optional — place on the <script> tag to render elsewhere"],
              ].map(([attr, ex, purpose]) => (
                <tr key={attr}>
                  <td className="px-4 py-2 font-mono text-blue-700 text-xs">{attr}</td>
                  <td className="px-4 py-2 font-mono text-slate-600 text-xs">{ex}</td>
                  <td className="px-4 py-2 text-slate-700 text-xs">{purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10 rounded-2xl bg-blue-50 border border-blue-100 p-5" data-testid="embed-preview-cta">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div>
            <div className="font-bold text-slate-900 inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-600" /> One conversation, everywhere in the network
            </div>
            <p className="text-xs text-slate-700 mt-1 max-w-2xl">
              Sister sites don't run their own forums — every discussion in ChatsPulse is scoped by category and location, so your page just filters into the master feed. No back-and-forth moderation.
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
            data-testid="embed-back-home"
          >
            Back to ChatsPulse
          </Link>
        </div>
      </section>
    </div>
  );
}

function ScenarioCard({ scenario, origin }) {
  const [copied, setCopied] = useState(false);
  const mountRef = useRef(null);
  const attrKeys = useMemo(() => Object.keys(scenario.attrs), [scenario]);

  // Fetch scoped threads and render inline. Doesn't depend on
  // embed.js — `renderInline` is a self-contained copy of the
  // widget's render() so preview loads instantly.
  useEffect(() => {
    let cancel = false;
    (async () => {
      const qs = new URLSearchParams();
      attrKeys.forEach((k) => qs.set(k.replace("data-", ""), scenario.attrs[k]));
      const url = `${origin}/api/chatpulse/v1/timeline?${qs.toString()}`;
      try {
        const r = await fetch(url);
        const data = await r.json();
        if (cancel) return;
        renderInline(mountRef.current, data.threads || [], scenario.attrs, origin);
      } catch (e) {
        if (cancel || !mountRef.current) return;
        mountRef.current.innerHTML = `<div style="color:#64748b;font-size:12px">Widget offline.</div>`;
      }
    })();
    return () => { cancel = true; };
  }, [attrKeys, scenario, origin]);

  const snippet = useMemo(() => {
    const attrs = Object.entries(scenario.attrs).map(([k, v]) => `${k}="${v}"`).join("\n     ");
    return `<div id="chatspulse-widget"\n     ${attrs}></div>\n<script src="${origin}/embed.js" async></script>`;
  }, [scenario, origin]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Snippet copied — paste anywhere.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy manually.");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`embed-scenario-${scenario.id}`}>
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">{scenario.title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{scenario.tagline}</div>
      </div>
      <div className="p-4 bg-slate-50/40">
        <div ref={mountRef} data-testid={`embed-mount-${scenario.id}`} />
      </div>
      <div className="p-4 border-t border-slate-100 bg-slate-900 text-slate-100 font-mono text-[11px] whitespace-pre overflow-x-auto">
        {snippet}
      </div>
      <button
        type="button"
        onClick={copy}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 border-t border-slate-100"
        data-testid={`embed-copy-${scenario.id}`}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied!" : "Copy snippet"}
      </button>
    </div>
  );
}

// Inline reproduction of embed.js's render() so we don't have to load
// the actual script + fight the singleton-boot behaviour.
function renderInline(mount, threads, attrs, origin) {
  if (!mount) return;
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const filters = Object.fromEntries(
    Object.entries(attrs).map(([k, v]) => [k.replace("data-", ""), v]),
  );
  if (!threads.length) {
    mount.innerHTML = `<div style="color:#64748b;font-size:12px;padding:10px;background:#f8fafc;border-radius:8px;text-align:center">No threads yet in this scope — <a href="${origin}/thread/new" style="color:#2563eb;font-weight:700">start one</a>.</div>`;
    return;
  }
  const items = threads.slice(0, Number(filters.limit || 5)).map((t) => {
    const loc = t.location || {};
    const placeBits = [loc.city, loc.state, loc.country].filter(Boolean).join(", ");
    return `<a href="${origin}/thread/${esc(t.id)}" target="_blank" rel="noopener noreferrer"
       style="display:flex;gap:10px;padding:8px 10px;border:1px solid #e2e8f0;border-radius:10px;text-decoration:none;color:#0f172a">
       <div style="flex:1;min-width:0">
         <div style="font-weight:600;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(t.title)}</div>
         ${placeBits ? `<div style="font-size:11px;color:#64748b;margin-top:2px">📍 ${esc(placeBits)}</div>` : ""}
       </div>
       <div style="font-size:11px;color:#64748b;white-space:nowrap">${t.post_count || 0} replies</div>
     </a>`;
  }).join("");
  mount.innerHTML = `<div style="display:flex;flex-direction:column;gap:6px">${items}</div>`;
}
